import { action, computed, makeObservable, observable } from "mobx";
import SwayyOrderAPI from "../api/SwayyOrderAPI";
import {
  GetSwayyOrdersParams,
  SwayyOrder,
  SwayyOrdersListData,
  SwayyOrdersPagination,
} from "../../../models/SwayyOrder/SwayyOrder";
import {
  SWAYY_ORDER_DEFAULT_STATUS_TAB,
  SWAYY_ORDER_STATUS_ALL_TAB,
  SWAYY_ORDER_STATUS_OPTIONS,
} from "../../../models/SwayyOrder/SwayyOrderFilters";

export const DEFAULT_SWAYY_ORDER_LIST_PARAMS: GetSwayyOrdersParams = {
  page: 1,
  limit: 20,
  q: "",
  status: SWAYY_ORDER_DEFAULT_STATUS_TAB,
  payment_status: "",
  channel: "",
  sort_by: "placed_at",
  sort_order: "desc",
  start_date: "",
  end_date: "",
};

const parseStatusParam = (value: string | null): string => {
  if (!value || value === SWAYY_ORDER_STATUS_ALL_TAB) return "";
  const statusIds = value.split(",").filter(Boolean);
  if (statusIds.length !== 1) return "";
  const isKnown = SWAYY_ORDER_STATUS_OPTIONS.some(
    (option) => option.id === statusIds[0],
  );
  return isKnown ? statusIds[0] : "";
};

export class SwayyOrderStore {
  swayyOrderAPI: SwayyOrderAPI;
  orders: SwayyOrder[];
  pagination: SwayyOrdersPagination;
  params: GetSwayyOrdersParams;
  filtersOpen: boolean;
  searchDraft: string;
  draftStatus: string[];
  draftPaymentStatus: string[];
  draftChannel: string[];
  draftStartDate: string;
  draftEndDate: string;
  loading: boolean;
  error: string | null;
  private fetchGeneration: number;

  constructor() {
    this.swayyOrderAPI = new SwayyOrderAPI();
    this.orders = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_ORDER_LIST_PARAMS };
    this.filtersOpen = false;
    this.searchDraft = "";
    this.draftStatus = this.params.status ? [this.params.status] : [];
    this.draftPaymentStatus = [];
    this.draftChannel = [];
    this.draftStartDate = "";
    this.draftEndDate = "";
    this.loading = false;
    this.error = null;
    this.fetchGeneration = 0;

    makeObservable(this, {
      orders: observable,
      pagination: observable,
      params: observable,
      filtersOpen: observable,
      searchDraft: observable,
      draftStatus: observable,
      draftPaymentStatus: observable,
      draftChannel: observable,
      draftStartDate: observable,
      draftEndDate: observable,
      loading: observable,
      error: observable,
      statusTab: computed,
      getOrders: action,
      patchOrder: action,
      setStatusTab: action,
      toggleFiltersOpen: action,
      setSearchDraft: action,
      applySearch: action,
      setDraftStatus: action,
      setDraftPaymentStatus: action,
      setDraftChannel: action,
      setDraftDateRange: action,
      applyFilters: action,
      clearFilters: action,
      setParams: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      setSort: action,
      initializeParamsFromSearch: action,
      buildSearchParams: action,
      reset: action,
    });
  }

  get statusTab(): string {
    const status = this.params.status || "";
    if (!status) return SWAYY_ORDER_STATUS_ALL_TAB;
    const isKnown = SWAYY_ORDER_STATUS_OPTIONS.some(
      (option) => option.id === status,
    );
    return isKnown ? status : SWAYY_ORDER_STATUS_ALL_TAB;
  }

  getOrders = async (
    params: GetSwayyOrdersParams = this.params,
  ): Promise<SwayyOrdersListData | null> => {
    const generation = ++this.fetchGeneration;
    this.loading = true;
    this.error = null;
    try {
      const response = await this.swayyOrderAPI.getOrders(params);
      // Ignore stale responses after status-tab (or filter) switches.
      if (generation !== this.fetchGeneration) return null;
      this.orders = response.orders;
      this.pagination = response.pagination;
      return response;
    } catch (error: any) {
      if (generation !== this.fetchGeneration) return null;
      this.error = error?.message || "Failed to fetch orders";
      throw error;
    } finally {
      if (generation === this.fetchGeneration) {
        this.loading = false;
      }
    }
  };

  patchOrder = (id: number, patch: Partial<SwayyOrder>) => {
    const index = this.orders.findIndex((order) => order.id === id);
    if (index < 0) return;
    this.orders[index] = { ...this.orders[index], ...patch };
  };

  setStatusTab = (statusId: string) => {
    const status =
      !statusId || statusId === SWAYY_ORDER_STATUS_ALL_TAB ? "" : statusId;
    if ((this.params.status || "") === status) return;

    this.orders = [];
    this.pagination = { ...this.pagination, total: 0, page: 1 };
    this.draftStatus = status ? [status] : [];
    this.params = {
      ...this.params,
      status,
      page: 1,
    };
  };

  initializeParamsFromSearch = (searchParams: URLSearchParams) => {
    const page = Math.max(
      1,
      parseInt(searchParams.get("page") || "1", 10) || 1,
    );
    const parsedLimit = parseInt(searchParams.get("limit") || "20", 10);
    const limit = [10, 20, 50, 100].includes(parsedLimit) ? parsedLimit : 20;
    const q = searchParams.get("q") || "";
    const rawStatus = searchParams.get("status");
    const status =
      rawStatus === null
        ? SWAYY_ORDER_DEFAULT_STATUS_TAB
        : parseStatusParam(rawStatus);
    const channel = searchParams.get("channel") || "";
    const startDate = searchParams.get("start_date") || "";
    const endDate = searchParams.get("end_date") || "";
    const sortBy = searchParams.get("sort_by") || "placed_at";
    const sortOrderParam = searchParams.get("sort_order");
    const sortOrder: "asc" | "desc" =
      sortOrderParam === "asc" || sortOrderParam === "desc"
        ? sortOrderParam
        : "desc";

    this.params = {
      page,
      limit,
      q,
      status,
      payment_status: "",
      channel,
      start_date: startDate,
      end_date: endDate,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    this.searchDraft = q;
    this.draftStatus = status ? [status] : [];
    this.draftPaymentStatus = [];
    this.draftChannel = channel ? channel.split(",").filter(Boolean) : [];
    this.draftStartDate = startDate;
    this.draftEndDate = endDate;
  };

  buildSearchParams = (): URLSearchParams => {
    const searchParams = new URLSearchParams();
    const { params } = this;

    if (params.page > 1) searchParams.set("page", String(params.page));
    if (params.limit && params.limit !== 20) {
      searchParams.set("limit", String(params.limit));
    }
    if (params.q) searchParams.set("q", params.q);
    searchParams.set(
      "status",
      params.status || SWAYY_ORDER_STATUS_ALL_TAB,
    );
    if (params.payment_status) {
      searchParams.set("payment_status", params.payment_status);
    }
    if (params.channel) searchParams.set("channel", params.channel);
    if (params.start_date) searchParams.set("start_date", params.start_date);
    if (params.end_date) searchParams.set("end_date", params.end_date);
    if (params.sort_by && params.sort_by !== "placed_at") {
      searchParams.set("sort_by", params.sort_by);
    }
    if (params.sort_order && params.sort_order !== "desc") {
      searchParams.set("sort_order", params.sort_order);
    }

    return searchParams;
  };

  toggleFiltersOpen = () => {
    this.filtersOpen = !this.filtersOpen;
  };

  setSearchDraft = (value: string) => {
    this.searchDraft = value;
  };

  setParams = (partial: Partial<GetSwayyOrdersParams>) => {
    this.params = { ...this.params, ...partial };
  };

  applySearch = () => {
    const q = this.searchDraft.trim();
    if (this.params.q === q) return;
    this.params = { ...this.params, q, page: 1 };
  };

  setDraftStatus = (status: string[]) => {
    this.draftStatus = status;
  };

  setDraftPaymentStatus = (paymentStatus: string[]) => {
    this.draftPaymentStatus = paymentStatus;
  };

  setDraftChannel = (channel: string[]) => {
    this.draftChannel = channel;
  };

  setDraftDateRange = (startDate: string, endDate: string) => {
    this.draftStartDate = startDate;
    this.draftEndDate = endDate;
  };

  applyFilters = () => {
    this.params = {
      ...this.params,
      payment_status: "",
      channel: this.draftChannel.join(","),
      start_date: this.draftStartDate,
      end_date: this.draftEndDate,
      page: 1,
    };
  };

  clearFilters = () => {
    this.searchDraft = "";
    this.draftPaymentStatus = [];
    this.draftChannel = [];
    this.draftStartDate = "";
    this.draftEndDate = "";
    this.params = {
      ...DEFAULT_SWAYY_ORDER_LIST_PARAMS,
      limit: this.params.limit,
      status: this.params.status,
    };
  };

  handlePageChange = (page: number) => {
    this.params = { ...this.params, page };
  };

  handleRowsPerPageChange = (limit: number) => {
    this.params = { ...this.params, limit, page: 1 };
  };

  setSort = (sortBy: string) => {
    const isSameColumn = this.params.sort_by === sortBy;
    const nextOrder =
      isSameColumn && this.params.sort_order === "asc" ? "desc" : "asc";

    this.params = {
      ...this.params,
      sort_by: sortBy,
      sort_order: isSameColumn ? nextOrder : "asc",
      page: 1,
    };
  };

  reset = () => {
    this.orders = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_ORDER_LIST_PARAMS };
    this.searchDraft = "";
    this.draftStatus = this.params.status ? [this.params.status] : [];
    this.draftPaymentStatus = [];
    this.draftChannel = [];
    this.draftStartDate = "";
    this.draftEndDate = "";
    this.filtersOpen = false;
    this.loading = false;
    this.error = null;
  };
}
